import AttachFileIcon from '@mui/icons-material/AttachFile';
import CachedIcon from '@mui/icons-material/Cached';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Divider } from '@mui/joy';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
  Fade,
  Skeleton,
  IconButton,
} from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useWebSocket, { ReadyState } from 'react-use-websocket';

import MessageAttachments from './messages/MessageAttachment';
import PendingAttachments from './messages/PendingAttachments';

import ComposeIcon from '../../../../../assets/svg/composeMessage.svg';
import deleteIcon from '../../../../../assets/svg/deleteIconRed.svg';
import { axiosInstance } from '../../../../../assets/utils/axiosInstance';
import { ROLE_VALUES } from '../../../../../constants/customerConstant';
import useDecryption from '../../../../../hooks/cryptography/decryptText';
import useEncryption from '../../../../../hooks/cryptography/encryptText';
import { useToastNotificationContext } from '../../../../../hooks/global/alert/useToastNotification';
import { Language } from '../../../../../interfaces/Language';
import MessagesObject, {
  FileMessageAttachment,
} from '../../../../../interfaces/MessagesObject';
import {
  addMessageId,
  clearPiiRemovedMessageRanges,
  setMessageboxId,
  setMessages,
  setPiiRemovedMessageRanges,
  setSelectedMessage,
  setWebSocketUrl,
} from '../../../../../slices/piiRemovalSlice';
import { setDecryptedMessages } from '../../../../../slices/reportDataSlice';
import { RootState } from '../../../../../utils/store';
import StandardTextEditor, {
  TranslatedDraft,
} from '../../../../common/editor/standardTextEditor';
import ExpandButton from '../../../../reportSubmission/fullscreenDescription/buttons/expandButton';
import DescriptionDialog from '../../../../reportSubmission/fullscreenDescription/dialog/descriptionDialog';
import '../../../../../assets/styles/formattedTextViewer.css';
import PiiRemovalMessageBox from '../../PiiRemovalMessageBox';

import { getFileExtension } from '~/assets/utils/helpers';
import { replacePiiDeleted } from '~/assets/utils/replacePiiDeleted';
import { ScheduledReportMeetings } from '~/pages/customer/reports/report-details/ReportMeetings';
import { FileUploadService } from '~/services/api/file-upload/endpoint';
import { isMutationSuccess } from '~/services/api/rest-api';
import { isMutationError } from '~/services/api/rest-api-error';

interface WSSendPreTranslatedMessage {
  message: string;
  untranslated_message: string;
  file_ids?: number[];
  to_user?: number;
  detected_languages_codes: string;
  action: string;
}

interface WSSendMessage {
  message: string;
  to_user?: number;
  action: string;
  file_ids?: number[];
}

interface WebSocketMessageFromAPI {
  id: string;
  from_user: number;
  to_user: number;
  message: string;
  is_translatable: boolean;
  created_at: string;
  files: FileMessageAttachment[];
}

interface TranslatedData {
  id: string;
  content: string | null;
  message: string;
  detected_languages: Array<Language>;
  is_pre_translated: boolean;
  target_language: Language;
}

interface SendTranslatedDraft {
  translatedMessageObject: TranslatedDraft | null;
  originalMessage: string;
}

export interface PendingFilesObject {
  file: File;
  id?: number;
  status: string;
}

const bodyText1 = {
  color: 'black',
  fontWeight: 'bold',
  fontSize: '1rem',
};
const bodyText2 = {
  color: 'grey',
  fontWeight: '400',
  fontSize: '.9rem',
};

const dateToString = (date: string) => {
  if (!isNaN(new Date(date).getDate())) {
    return new Date(date).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  } else return '--';
};

const MessagesTab = () => {
  const MAX_FILE_SIZE = 26214400;
  const userType = localStorage.getItem('user_type');

  const intl = useIntl();
  const [userId, setUserId] = useState(0);
  const [showMessageField, setShowMessageField] = useState<boolean>(false);
  const [text, setText] = useState<string>('');
  const [messageHistory, setMessageHistory] = useState<Array<MessagesObject>>(
    [],
  );
  const [isProcessing, setIsProcessing] = useState(true);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  const {
    piiRemovalFlag,
    messages,
    selectedMessage,
    piiRemovedMessageRanges,
    // Message Received Translation
  } = useSelector((state: RootState) => state.piiRemoval);

  const reportDetails = useSelector(
    (state: RootState) => state.reportData.report,
  );

  const userAccessRoles = useSelector(
    (state: RootState) => state.reportData.userAccessRoles,
  );

  const [showTranslateButton, setShowTranslateButton] = useState(false);
  const [translatedContent, setTranslatedContent] = useState<
    Array<TranslatedData>
  >([]);
  const [isLoadingTranslate, setIsLoadingTranslate] = useState(false);
  const [seeOriginalAnchorEl, setSeeOriginalAnchorEl] =
    useState<null | HTMLElement>(null);
  const seeOriginalOpen = Boolean(seeOriginalAnchorEl);
  const [seeOriginal, setSeeOriginal] = useState(false);
  // Draft Message Translation
  const [loadingDraftTranslation, setLoadingDraftTranslation] = useState(false);
  const [draftLanguage, setDraftLanguage] = useState('');
  const [switchDraftMessage, setSwitchDraftMessage] = useState(false);
  const [originalDraftMessage, setOriginalDraftMessage] = useState<string>('');
  const [translatedDraftMessage, setTranslatedDraftMessage] =
    useState<string>('');
  const [translatedDraftObject, setTranslatedDraftObject] =
    useState<SendTranslatedDraft>();

  const [pendingFiles, setPendingFiles] = useState<PendingFilesObject[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [accessToken, setAccessToken] = useState<string | null>();
  const { createToastNotification } = useToastNotificationContext();
  const { decryptText } = useDecryption();
  const { encryptText } = useEncryption();
  const [isUploadingAttachment, setIsUploadingAttachment] =
    useState<boolean>(false);
  const [uploadAttachment] =
    FileUploadService.endpoints.uploadMessageAttachmentProcess.useMutation();
  const [addedPendingFiles, setAddedPendingFiles] = useState<boolean>(false);
  const [isErrorOnUpload, setIsErrorOnUpload] = useState<{
    isError: boolean;
    message: string;
  }>({
    isError: false,
    message: '',
  });

  useEffect(() => {
    const userTypes = [
      ROLE_VALUES.TENANT_ADMIN,
      ROLE_VALUES.PARTNER_USER,
      ROLE_VALUES.PARTNER_ADMIN,
    ];
    const userRole = parseInt(localStorage.getItem('user_type') || '2', 10);
    if (userTypes.includes(userRole)) {
      setIsAdmin(true);
    }
  }, []);

  useEffect(() => {
    messageHistory.map((message, index) => {
      dispatch(
        setMessages([
          {
            index: index,
            messageId: message.messageId,
            messageContent: message.content ? message.content : 'no content',
            name: message.name,
            createdAt: message.createdAt,
          },
        ]),
      );
    });
  }, [messageHistory]); //eslint-disable-line

  //Extracts data from any cookie name available in the Cookie Document
  const getCookie = (name: string): string | null => {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      // Check if the cookie starts with the desired name
      if (cookie.indexOf(nameEQ) === 0) {
        // Get the value of the cookie
        const value = cookie.substring(nameEQ.length);
        return value;
      }
    }
    return null;
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const isTextEmpty = () => {
    return text.replace(/<[^>]*>?/gm, '').trim().length === 0;
  };

  const textChangeHandler = (content: string) => {
    setText(content);
  };

  const translatedDraftObjectHandler = (
    originalMessage: string,
    translatedObject: TranslatedDraft | null = null,
  ) => {
    setTranslatedDraftObject({
      translatedMessageObject: translatedObject,
      originalMessage: originalMessage,
    });
  };

  const messagesEndRef = useRef<HTMLInputElement>(null);

  const WS_PATH = import.meta.env.VITE_WS_PATH;
  const LINK = '/report-chat';
  const CONVERSATION_PATH = `/${reportDetails.conversation}`;
  const apiUrl = `${
    import.meta.env.VITE_WSS_PROTOCOL
  }${WS_PATH}${LINK}${CONVERSATION_PATH}/`;
  const SOCKET_URL = `${apiUrl}`;

  const messageIdArray = useSelector(
    (state: RootState) => state.piiRemoval.removedMessagesId,
  );
  const userTranslationLanguage = useSelector(
    (state: RootState) => state.languages.userTranslationLanguage,
  );
  const region = useSelector((state: RootState) => state.auth.user.region);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setWebSocketUrl(SOCKET_URL));
  }, [SOCKET_URL, dispatch]);

  const { sendMessage, lastMessage, readyState } = useWebSocket(SOCKET_URL, {
    //Will attempt to reconnect on all close events, such as server shutting down
    shouldReconnect: () => true, // closeEvent
    queryParams: {
      token: `${accessToken}`,
      language_id: userTranslationLanguage.code,
      region: region ? region : 'eu',
    },
  });

  //This function checks if the message ID passed is located within the stored array in redux for message ID's to be deleted
  const checkMessageId = (messageId: string) => {
    return messageIdArray.includes(messageId); //If returns true then it is an id within the array of message ID's that are to be deleted
  };

  const clickSendMessageHandler = useCallback(
    (messageText: string) => {
      if (messageText !== '') {
        // Description in-transit encryption preparation
        if (translatedDraftObject?.translatedMessageObject) {
          // Send message with pre translated
          const chat: WSSendPreTranslatedMessage = {
            message: translatedDraftObject.translatedMessageObject.message,
            file_ids: pendingFiles
              .filter(
                (file) => file.status === 'success' && file.id !== undefined,
              )
              .map((file) => file.id!),
            untranslated_message: translatedDraftObject.originalMessage,
            detected_languages_codes:
              translatedDraftObject.translatedMessageObject.target_language
                .code,
            action: 'create',
          };
          sendMessage(JSON.stringify(chat));
        } else {
          encryptText(messageText).then((encryptedText) => {
            const chat: WSSendMessage = {
              message: encryptedText,
              action: 'create',
              file_ids: pendingFiles
                .filter(
                  (file) => file.status === 'success' && file.id !== undefined,
                )
                .map((file) => file.id!),
            };
            sendMessage(JSON.stringify(chat));
          });
        }
        setOpenDialog(false);
        setText('');
        setDraftLanguage('');
        setOriginalDraftMessage('');
        setSwitchDraftMessage(false);
        setTranslatedDraftObject(undefined);
        setTranslatedDraftMessage('');
        setShowMessageField(false);
        setPendingFiles([]);
      }
    },
    [
      encryptText,
      sendMessage,
      pendingFiles,
      translatedDraftObject?.originalMessage,
      translatedDraftObject?.translatedMessageObject,
    ],
  );

  const fetchTranslatedMessages = (url: string) => {
    setIsLoadingTranslate(true);
    axiosInstance()
      .get(url)
      .then((response) => {
        const translatedMessages = response.data.results;
        if (response.data.count !== translatedContent.length) {
          translatedMessages.map((results: TranslatedData, index: number) => {
            if (
              translatedContent.length === 0 ||
              !translatedContent.some((item) => item.id === results.id)
            ) {
              decryptText(results.message)
                .then((decryptedText) => {
                  setTranslatedContent((prevState) => [
                    ...prevState,
                    {
                      content: decryptedText,
                      message: results.message,
                      id: results.id,
                      detected_languages: results.detected_languages,
                      is_pre_translated: results.is_pre_translated,
                      target_language: results.target_language,
                    },
                  ]);
                })
                .finally(() => {
                  if (index === response.data.results.length - 1) {
                    if (response.data.next !== null) {
                      fetchTranslatedMessages(
                        `https${response.data.next.split('http')[1]}`,
                      );
                    } else {
                      createToastNotification(
                        intl.formatMessage({
                          defaultMessage: 'Messages translated successfully',
                        }),
                        'success',
                      );
                      setIsLoadingTranslate(false);
                    }
                  }
                });
            } else {
              setIsLoadingTranslate(false);
              createToastNotification(
                intl.formatMessage({
                  defaultMessage: 'Messages translated successfully',
                }),
                'success',
              );
            }
          });
        } else {
          setIsLoadingTranslate(false);
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Messages translated successfully',
            }),
            'success',
          );
        }
      })
      .catch((error) => {
        setTranslatedContent([]);
        throw new Error(error);
      });
  };

  const loadingTranslation = useCallback(
    (id: string) => {
      if (
        isLoadingTranslate &&
        !translatedContent.some((item) => item.id === id)
      ) {
        return true;
      } else return false;
    },
    [isLoadingTranslate, translatedContent],
  );

  const setMessage = useCallback(
    (messageId: string, content: string) => {
      const contentTranslated = translatedContent.find(
        (messageContent) => messageContent.id === messageId,
      );
      if (!seeOriginal) {
        return contentTranslated ? contentTranslated.content ?? '' : content;
      } else {
        return content;
      }
    },
    [seeOriginal, translatedContent],
  );

  const handleSeeOriginalClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setSeeOriginalAnchorEl(event.currentTarget);
  };

  const handleSeeOriginalClose = () => {
    setSeeOriginalAnchorEl(null);
  };

  const handleSeeOriginal = () => {
    setSeeOriginal(!seeOriginal);
  };

  const handleTranslate = () => {
    setSeeOriginal(false);
    fetchTranslatedMessages(
      `/report-chat-messages?page=1&page_size=10&conversation_id=${reportDetails.conversation}&target_language_id=${userTranslationLanguage.id}`,
    );
  };

  useEffect(() => {
    const wsAccessDev = localStorage.getItem('ws_access');
    const extractedAccessToken = getCookie('ws_access_token');
    if (wsAccessDev) {
      setAccessToken(wsAccessDev);
    } else if (extractedAccessToken !== null) {
      setAccessToken(extractedAccessToken);
    }
    //ts-ignore
  }, []);

  useEffect(() => {
    const fetchMessages = async () => {
      const localizedYou = intl.formatMessage({ defaultMessage: 'You ' });
      const localizedSender = intl.formatMessage({ defaultMessage: 'Sender' });
      if (lastMessage !== null) {
        const websocketData = JSON.parse(lastMessage.data);
        if (
          websocketData.type === 'chat.history' &&
          websocketData.messages.length > 0 &&
          messageHistory.length === 0 &&
          userId === 0
        ) {
          setUserId(parseInt(websocketData.user_id, 10));
          if (websocketData.is_translatable) setShowTranslateButton(true);
          websocketData.messages.map(
            (
              {
                message,
                from_user: fromUser,
                created_at: createdAt,
                id: messageId,
                files,
              }: WebSocketMessageFromAPI,
              index: number,
            ) => {
              // Description in-transit decryption before displaying
              decryptText(message)
                .then((decryptedText) => {
                  setMessageHistory((prevState) => [
                    ...prevState,
                    {
                      content: decryptedText || '',
                      name:
                        fromUser === websocketData.user_id
                          ? localizedYou
                          : localizedSender,
                      createdAt,
                      messageId,
                      files,
                    },
                  ]);
                })
                .finally(() => {
                  if (index === websocketData.messages.length - 1)
                    setIsProcessing(false);
                });
            },
          );
        }
        if (websocketData.type === 'chat.message') {
          // Temp solution for duplicating sent messages and received messages
          if (
            !messageHistory.some((item) => item.messageId === websocketData.id)
          ) {
            if (
              websocketData.is_translatable ||
              websocketData.is_pre_translated
            )
              setShowTranslateButton(true);
            const {
              message,
              from_user: fromUser,
              created_at: createdAt,
              id: messageId,
              files,
            } = websocketData;
            decryptText(message).then((decryptedText) => {
              setMessageHistory((prevState) => [
                ...prevState,
                {
                  content: decryptedText || '',
                  name: fromUser === userId ? localizedYou : localizedSender,
                  createdAt,
                  messageId,
                  files,
                },
              ]);
            });
          }
        }
        if (websocketData.messages?.length === 0) setIsProcessing(false);
      }
    };

    fetchMessages();
  }, [decryptText, intl, lastMessage, messageHistory, userId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  //sets the message history when new message is sent
  useEffect(() => {
    if (!isProcessing) {
      scrollToBottom();
      dispatch(setDecryptedMessages(messageHistory));
    }
  }, [messageHistory, isProcessing, dispatch]);

  const handleChooseFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Trigger click event on the input
    }
  };

  const uploadFile = useCallback(
    async (file: File) => {
      try {
        // Upload file and get response
        const res = await uploadAttachment({
          conversationId: reportDetails.conversation,
          file: file,
          startUploadData: {
            file_name: file.name,
            file_type: getFileExtension(file.name),
            file_size: file.size,
          },
        });
        // Check if upload was successful
        if (isMutationSuccess(res) && res.data.id) {
          return {
            id: res.data.id,
            file: file,
            status: 'success',
          };
        } else {
          let status = 'failed';
          if (isMutationError(res) && res.error.status === StatusCodes.FORBIDDEN) {
            status = 'no_retry';

            createToastNotification(
              res.error.data.message,
              'error',
            );
          }

          return {
            file: file,
            status: status,
          };
        }
      } catch (error) {
        return {
          file: file,
          status: 'failed',
        };
      }
    },
    [reportDetails.conversation, uploadAttachment, createToastNotification],
  );

  const uploadAllFiles = useCallback(async () => {
    setIsUploadingAttachment(true);
    const files = [...pendingFiles];
    const promises = files.map(async (file) => {
      if (['loading', 'failed', 'no_retry'].includes(file.status)) {
        const result = await uploadFile(file.file);
        return {
          ...file,
          ...result,
        };
      }
      return file;
    });

    // Execute all upload promises
    const updatedFiles = await Promise.all(promises);
    const validUpdatedFiles = updatedFiles.filter((file) => file.status !== 'no_retry');  // prevents retry of invalid files
    setAddedPendingFiles(false);
    setPendingFiles(validUpdatedFiles);
    setIsUploadingAttachment(false);
  }, [pendingFiles, uploadFile]);

  const isFileSizeValidated = (files: File[]): boolean => {
    let isError: boolean = false;
    files.map((file) => {
      if (file.size >= MAX_FILE_SIZE) {
        isError = true;
      }
    });
    if (isError) {
      setIsErrorOnUpload({
        isError: true,
        message: intl.formatMessage({
          defaultMessage:
            'The files you have selected is too large. The maximum size is 25MB. Compress your files to reduce its size.',
        }),
      });
      return false;
    }
    setIsErrorOnUpload({
      isError: false,
      message: '',
    });
    return true;
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles = [...files];
    if (!isFileSizeValidated(newFiles)) return;
    setPendingFiles((prevPendingFiles) => [
      ...prevPendingFiles,
      ...newFiles.map((file) => ({
        file: file,
        status: 'loading',
      })),
    ]);
    setAddedPendingFiles(true);
  };

  useEffect(() => {
    if (addedPendingFiles) uploadAllFiles();
  }, [addedPendingFiles, uploadAllFiles]);

  const removePendingFile = (key: number) => {
    const files = [...pendingFiles];
    files.splice(key, 1);
    setPendingFiles(files);
  };

  const handleHighlight = () => {
    const selection = window.getSelection();
    if (selection) {
      const range = selection.getRangeAt(0);
      const startContainer = range.startContainer;
      const endContainer = range.endContainer;

      const piiDeletedRanges: [number, number][] = [];
      const piiDeletedRegex = /\*PII Deleted\*/g;
      let match;

      while (
        (match = piiDeletedRegex.exec(selectedMessage.messageContent)) !== null
      ) {
        piiDeletedRanges.push([match.index, match.index + match[0].length]);
      }
      // Get the full text content
      const fullText = selectedMessage.messageContent;

      // Calculate the start and end positions
      let startPosition = range.startOffset;
      let endPosition = range.endOffset;

      // Count characters before the start position
      if (startContainer && startContainer.nodeType === Node.TEXT_NODE) {
        const startText = (startContainer as Text).textContent || '';
        startPosition += fullText.indexOf(startText);
      }

      // Count characters before the end position
      if (endContainer && endContainer.nodeType === Node.TEXT_NODE) {
        const endText = (endContainer as Text).textContent || '';
        endPosition += fullText.indexOf(endText);
      }

      // Check if the selected range overlaps with any existing highlighted positions
      const messageRange = piiRemovedMessageRanges?.find(
        (positionRange) =>
          positionRange.messageId === selectedMessage.messageId,
      );

      const overlappingPosition = messageRange
        ? messageRange.contentPiiRemovedRanges?.find(
            ([start, end]) =>
              (start <= startPosition && end >= startPosition) ||
              (start <= endPosition && end >= endPosition),
          )
        : null;

      const checkForDeletedPositions = piiDeletedRanges.find(
        ([start, end]) =>
          (start <= startPosition && end >= startPosition) ||
          (start <= endPosition && end >= endPosition),
      );

      if (overlappingPosition) {
        createToastNotification(
          intl.formatMessage({
            defaultMessage:
              'This area has already been selected to be removed.',
          }),
          'error',
        );
      } else if (checkForDeletedPositions) {
        createToastNotification(
          intl.formatMessage({
            defaultMessage: 'You cannot delete PII Deleted text.',
          }),
          'error',
        );
      } else if (
        (startPosition <= 1 && endPosition <= 1) ||
        startPosition == null ||
        endPosition == null
      ) {
        createToastNotification(
          intl.formatMessage({
            defaultMessage:
              'Pii Removal Not Complete: Ensure you select and highlight the text you want to remove within the messages.',
          }),
          'error',
        );

        startPosition = 0;
        endPosition = 0;
      } else {
        // Create a new array with updated positions
        const updatedPositions: [number, number] = [startPosition, endPosition];

        // Dispatch an action to update the Redux state
        if (updatedPositions !== undefined && selectedMessage.messageId) {
          const payload = {
            messageId: selectedMessage.messageId,
            contentPiiRemovedRanges: [updatedPositions],
            messageContent: fullText,
            index: selectedMessage.messageIndex,
          };
          dispatch(setPiiRemovedMessageRanges(payload));
        }
      }
      return;
    }
  };

  const SendButton = () => (
    <Stack
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 'auto',
      }}
    >
      <IconButton
        onClick={handleChooseFileClick}
        disabled={isUploadingAttachment}
      >
        <AttachFileIcon />
      </IconButton>
      <input
        id='file-upload'
        type='file'
        style={{ display: 'none' }}
        multiple
        onChange={handleFileChange}
        ref={fileInputRef}
      />
      <Button
        variant='contained'
        sx={{
          maxWidth: '150px',
          padding: '10px 30px',
          borderRadius: '50px',
          alignItems: 'center',
          bgcolor: '#2449FC',
          textTransform: 'none',
          height: '34px',
        }}
        onClick={() => clickSendMessageHandler(text)}
        disabled={isTextEmpty() || isUploadingAttachment}
      >
        <FormattedMessage defaultMessage='Send' />
      </Button>
    </Stack>
  );

  const concatMessageId = (messageId: string) => {
    dispatch(addMessageId(messageId));
  };

  const CloseButton = () => (
    <Button
      variant='text'
      onClick={() => {
        setOpenDialog(false);
        setShowMessageField(false);
      }}
      sx={{
        height: '2rem',
        padding: '0.85rem',
        borderRadius: '4px',
        gap: '0.625rem',
        color: '#878787',
        '& .MuiButton-startIcon': {
          marginRight: '0px',
        },
        textTransform: 'none',
      }}
      disableElevation
      startIcon={<CloseIcon htmlColor='#878787' />}
    >
      <Typography
        sx={{
          fontFamily: 'Schibsted Grotesk',
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '17px',
          letterSpacing: '0em',
          color: '#878787',
          my: '0.5rem',
          display: { xs: 'none', sm: 'initial' },
        }}
      >
        <FormattedMessage defaultMessage='Close' />
      </Typography>
    </Button>
  );

  return (
    <Box
      sx={{
        height: 'auto',
        position: 'relative',
        border: piiRemovalFlag ? '1px solid' : '',
        borderRadius: '6px',
        margin: piiRemovalFlag ? '20px' : '',
        padding: piiRemovalFlag ? '10px' : '',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: showMessageField ? 'auto' : '550px',
          scrollbarWidth: 'thin',
          overflowY: !isProcessing ? 'auto' : 'hidden',
          padding: '1rem',
          '::-webkit-scrollbar': {
            width: '10px',
          },
          '::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(155, 155, 155, 0.5)',
            borderRadius: ' 20px',
            border: ' transparent',
          },
          minHeight: '220px',
        }}
      >
        <div className='flex max-h-48 shrink-0 overflow-auto w-full'>
          <ScheduledReportMeetings />
        </div>
        <Divider sx={{ mb: 2, mt: 1 }} />
        <div className='relative h-full'>
          {/* When the message history being fetched is 3 or more, the skeleton loader will be displayed */}
          {isProcessing && messageHistory.length >= 3
            ? messageHistory.map((message) => (
                <Box key={message.messageId} sx={{ overflow: 'hidden' }}>
                  <Skeleton variant='text' width={100} height={20} />
                  <Skeleton variant='text' width={150} height={15} />
                  <Skeleton variant='rectangular' height={150} sx={{ mt: 1 }} />
                </Box>
              ))
            : messageHistory.length !== 0 &&
              isProcessing && (
                <Box textAlign='center'>
                  <Typography sx={{ color: '#00CECF', fontSize: 14 }}>
                    <FormattedMessage defaultMessage='Fetching messages' />
                    <CircularProgress sx={{ ml: 1 }} size={10} />
                  </Typography>
                </Box>
              )}
          {Array.isArray(messages) &&
            !isProcessing &&
            messages.map((message, key) => {
              return (
                <Fade in={true} key={key}>
                  <Box sx={{ wordWrap: 'break-word' }}>
                    <Typography
                      sx={{
                        ...bodyText1,
                        textDecoration:
                          checkMessageId(message.messageId) && piiRemovalFlag
                            ? 'line-through'
                            : '',
                      }}
                    >
                      {message.name !== ' '
                        ? message.name
                        : intl.formatMessage({ defaultMessage: 'Anonymous' })}
                    </Typography>
                    <Stack sx={{ flexDirection: 'row' }}>
                      <Typography
                        sx={{
                          ...bodyText2,
                          textDecoration:
                            checkMessageId(message.messageId) && piiRemovalFlag
                              ? 'line-through'
                              : '',
                        }}
                      >
                        {message
                          ? dateToString(
                              message.createdAt ? message.createdAt : '',
                            )
                          : ''}
                        {loadingTranslation(message.messageId) && (
                          <CircularProgress size={10} sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      {piiRemovalFlag === true &&
                      userType === ROLE_VALUES.TENANT_ADMIN.toString() ? (
                        <Button
                          onClick={() => concatMessageId(message.messageId)}
                          disableTouchRipple
                          sx={{
                            width: '20px',
                            height: '20px',
                            marginLeft: 'auto',
                          }}
                        >
                          <img src={deleteIcon} alt='delete-icon' />
                        </Button>
                      ) : (
                        ''
                      )}
                    </Stack>

                    {piiRemovalFlag ? (
                      <Box
                        onClick={() => {
                          dispatch(setMessageboxId(message.messageId));
                          dispatch(
                            setSelectedMessage(
                              piiRemovalFlag
                                ? {
                                    messageIndex: key,
                                    messageId: message.messageId,
                                    messageContent: message.messageContent,
                                  }
                                : {
                                    messageIndex: 0,
                                    messageId: '',
                                    messageContent: '',
                                  },
                            ),
                          );
                        }}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor:
                            selectedMessage.messageIndex === key
                              ? '#f0f0f0'
                              : 'transparent',
                          borderRadius: '6px',
                        }}
                      >
                        <PiiRemovalMessageBox
                          // As props provide the messageID, messageContent
                          messageContent={setMessage(
                            message.messageId,
                            message.messageContent,
                          )}
                          messageId={message.messageId}
                          messageIndex={key}
                          key={key}
                        />
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          textDecoration:
                            checkMessageId(message.messageId) && piiRemovalFlag
                              ? 'line-through'
                              : '',
                          height: 'auto',
                        }}
                        dangerouslySetInnerHTML={{
                          __html: replacePiiDeleted(
                            setMessage(
                              message.messageId,
                              message.messageContent,
                            ),
                          ),
                        }}
                        className={'formatted-text-viewer'}
                      />
                    )}

                    {messageHistory.map((msg, index) => {
                      if (msg.messageId === message.messageId) {
                        return (
                          <MessageAttachments
                            key={index}
                            attachments={msg.files ?? []}
                          />
                        );
                      }
                    })}
                  </Box>
                </Fade>
              );
            })}

          {Array.isArray(messageHistory) && messageHistory.length === 0 && (
            <Box textAlign='center'>
              <Typography sx={{ fontSize: 14 }}>
                <FormattedMessage defaultMessage='No Message Found' />
              </Typography>
            </Box>
          )}

          <Box ref={messagesEndRef} />

          {showTranslateButton && !isProcessing && !piiRemovalFlag && (
            <>
              <ButtonGroup
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                }}
              >
                <Button
                  disabled={
                    userAccessRoles.is_read_only_report || isLoadingTranslate
                  }
                  startIcon={<CachedIcon />}
                  sx={{
                    textTransform: 'none',
                    fontSize: 14,
                    color: '#00CECF',
                    bgcolor: '#E0F9F9',
                    borderTop: 'none',
                    borderBottom: 'none',
                    borderLeft: 'none',
                    borderRight: '1px solid #00CECF',
                    '&:hover': {
                      background: '#E0F9F9',
                      borderTop: 'none',
                      borderBottom: 'none',
                      borderLeft: 'none',
                      borderRight: '1px solid #00CECF',
                    },
                  }}
                  onClick={() => handleTranslate()}
                >
                  {!isLoadingTranslate ? (
                    <Typography fontSize={14}>
                      <FormattedMessage defaultMessage='Translate' />
                    </Typography>
                  ) : (
                    <Typography fontSize={14}>
                      <FormattedMessage defaultMessage='Translating...' />
                    </Typography>
                  )}
                </Button>
                <Button
                  disabled={
                    userAccessRoles.is_read_only_report || isLoadingTranslate
                  }
                  sx={{
                    textTransform: 'none',
                    fontSize: 14,
                    color: '#00CECF',
                    bgcolor: '#E0F9F9',
                    borderTop: 'none',
                    borderBottom: 'none',
                    borderLeft: '1px solid #00CECF',
                    borderRight: 'none',
                    '&:hover': {
                      background: '#E0F9F9',
                      borderTop: 'none',
                      borderBottom: 'none',
                      borderLeft: '1px solid #00CECF',
                      borderRight: 'none',
                    },
                  }}
                  size='small'
                  onClick={handleSeeOriginalClick}
                >
                  <ExpandMoreIcon sx={{ transform: 'rotate(180}deg)' }} />
                </Button>
              </ButtonGroup>

              <Popover
                sx={{ marginTop: '5px' }}
                open={seeOriginalOpen}
                anchorEl={seeOriginalAnchorEl}
                onClose={handleSeeOriginalClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '246px',
                    maxHeight: '200px',
                    overflow: 'auto',
                    borderRadius: '6px',
                    backgroundColor: '#FFF',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <FormControlLabel
                      disabled={translatedContent.length === 0}
                      control={
                        <Checkbox
                          checked={seeOriginal}
                          onChange={handleSeeOriginal}
                          sx={{
                            ml: 1,
                            '&.Mui-checked': {
                              color: '#00CECF',
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            color:
                              translatedContent.length !== 0
                                ? '#181818'
                                : '#878787',
                            fontFamily: 'Schibsted Grotesk',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                          }}
                        >
                          <FormattedMessage defaultMessage='See Original' />
                        </Typography>
                      }
                    />
                  </Box>
                </div>
              </Popover>
            </>
          )}
        </div>
        {/* FAB button */}
        {!piiRemovalFlag && !showMessageField && (
          <Button
            disabled={
              userAccessRoles.is_read_only_report ||
              !(
                userAccessRoles.is_allowed_to_send_messages ||
                isAdmin ||
                reportDetails.isAssignee
              )
            }
            variant='contained'
            sx={{
              padding: '12px',
              gap: '8px',
              bgcolor: '#2449FC',
              position: 'absolute',
              right: 25,
              bottom: 16,
              textTransform: 'none',
              borderRadius: '12px',
              boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.20)',
            }}
            onClick={() => {
              setShowMessageField(true);
              setTimeout(() => {
                scrollToBottom();
              }, 100);
            }}
          >
            <Box
              component='img'
              sx={{ height: '20px' }}
              src={ComposeIcon}
            ></Box>
            <Typography fontSize={14}>
              <FormattedMessage defaultMessage='Compose' />
            </Typography>
          </Button>
        )}
      </Box>
      {showMessageField && (
        <>
          <Box
            sx={{
              width: 'inherit',
              borderRadius: '6px',
              border: '1px solid #DADADA',
              '&:focus-within': {
                border: '1px solid #181818',
              },
              backgroundColor: 'white',
              position: 'sticky',
              left: '1%',
              right: '1%',
              bottom: '1%',
            }}
          >
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              sx={{
                padding: '4px 12px',
                backgroundColor: '#F6F6F6',
                borderRadius: '6px 6px 0px 0px',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  color: '#878787',
                }}
              ></Typography>
              <Stack direction='row' alignItems='center' spacing={0}>
                <ExpandButton
                  handleButtonClicked={handleOpenDialog}
                  variant='dark'
                />
                <CloseButton />
              </Stack>
            </Stack>
            <DescriptionDialog
              text={text}
              onChangeText={textChangeHandler}
              open={openDialog}
              setClose={handleCloseDialog}
              variant='message'
              messageButton={<SendButton />}
              closeButton={<CloseButton />}
              attachedItems={
                pendingFiles && (
                  <PendingAttachments
                    pendingFiles={pendingFiles}
                    removePendingFile={removePendingFile}
                    retryUpload={uploadAllFiles}
                    error={isErrorOnUpload}
                    fullWidth
                  />
                )
              }
              enableTranslate={true}
              isEditable={readyState === ReadyState.OPEN && !isProcessing}
              hideTitle
              translatedDraftMessageVariables={{
                loadingDraftTranslation,
                setLoadingDraftTranslation,
                draftLanguage,
                setDraftLanguage,
                switchDraftMessage,
                setSwitchDraftMessage,
                originalDraftMessage,
                setOriginalDraftMessage,
                translatedDraftMessage,
                setTranslatedDraftMessage,
              }}
            />
            <StandardTextEditor
              text={text}
              onChangeText={textChangeHandler}
              customPlaceholder={intl.formatMessage({
                defaultMessage: 'Type here...',
              })}
              variant='message'
              messageButton={<SendButton />}
              attachedItems={
                pendingFiles && (
                  <PendingAttachments
                    pendingFiles={pendingFiles}
                    removePendingFile={removePendingFile}
                    retryUpload={uploadAllFiles}
                    error={isErrorOnUpload}
                  />
                )
              }
              enableTranslate={true}
              isEditable={readyState === ReadyState.OPEN && !isProcessing}
              setTranslatedDraftObject={translatedDraftObjectHandler}
              translatedDraftMessageVariables={{
                loadingDraftTranslation,
                setLoadingDraftTranslation,
                draftLanguage,
                setDraftLanguage,
                switchDraftMessage,
                setSwitchDraftMessage,
                originalDraftMessage,
                setOriginalDraftMessage,
                translatedDraftMessage,
                setTranslatedDraftMessage,
              }}
            />
          </Box>
        </>
      )}{' '}
      {piiRemovalFlag ? (
        <div>
          <Divider
            sx={{
              width: 'auto',
            }}
          />
          <Button
            sx={{ color: '#5D5D5D', textTransform: 'none' }}
            onClick={() => handleHighlight()}
          >
            <FormattedMessage defaultMessage='Highlight for Removal' />
          </Button>
          <Button
            sx={{ color: '#5D5D5D', textTransform: 'none' }}
            onClick={() => {
              dispatch(clearPiiRemovedMessageRanges());
            }}
          >
            <FormattedMessage defaultMessage='Remove All Highlights' />
          </Button>
        </div>
      ) : (
        ''
      )}
    </Box>
  );
};

export default MessagesTab;
